import React, { TextareaHTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const textareaVariants = cva(
  'flex min-h-[80px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 resize-none',
  {
    variants: {
      variant: {
        default: 'hover:border-ring/50',
        ghost: 'border-transparent bg-muted/50 hover:bg-muted',
        filled: 'bg-muted border-transparent focus-visible:bg-background',
      },
      textareaSize: {
        sm: 'min-h-[60px] px-2 py-1 text-xs',
        md: 'min-h-[80px] px-3 py-2',
        lg: 'min-h-[120px] px-4 py-3 text-base',
        xl: 'min-h-[200px] px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      textareaSize: 'md',
    },
  }
);

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  label?: string;
  description?: string;
  error?: string;
  showCharCount?: boolean;
  success?: boolean;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, variant, textareaSize, label, description, error, showCharCount, success, maxLength, value, ...props }, ref) => {
    const textareaId = props.id || `textarea-${Math.random().toString(36).substring(7)}`;
    const currentLength = value?.toString().length || 0;
    
    return (
      <div className="w-full space-y-2">
        {label && (
          <label 
            htmlFor={textareaId}
            className="text-sm font-medium leading-none text-foreground"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            id={textareaId}
            className={cn(
              textareaVariants({ variant, textareaSize }),
              error && 'border-destructive focus-visible:ring-destructive',
              success && 'border-green-500 focus-visible:ring-green-500',
              className
            )}
            ref={ref}
            value={value}
            maxLength={maxLength}
            {...props}
          />
          {success && (
            <div className="absolute top-3 right-3 text-green-500">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center">
          <div className="flex-1">
            {description && !error && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            )}
          </div>
          {showCharCount && maxLength && (
            <p className={cn(
              "text-xs tabular-nums",
              currentLength >= maxLength * 0.9 && currentLength < maxLength && "text-amber-500",
              currentLength >= maxLength && "text-destructive",
              currentLength < maxLength * 0.9 && "text-muted-foreground"
            )}>
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
// PHASE-13 START
// Promotion shim: always delegate to TextAreaV2 while preserving label/char count API.
import { TextAreaV2 as __TextAreaV2 } from './TextAreaV2';
type TextAreaV2Size = 'sm' | 'md' | 'lg' | 'xl';

const ExportedTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
  const {
    className,
    variant,
    textareaSize,
    label,
    description,
    error,
    showCharCount,
    success: _success,
    maxLength,
    value,
    ...rest
  } = props as any;

  const sizeMap: Record<string, TextAreaV2Size> = { sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' };
  const mappedSize = sizeMap[textareaSize as string] || 'md';
  const mappedVariant = (variant === 'default' || !variant) ? 'default' : 'default';

  const currentLength = (value?.toString()?.length ?? 0) as number;

  return (
    <div className="w-full space-y-2">
      {label && (
        <label className="text-sm font-medium leading-none text-foreground">
          {label}
        </label>
      )}
      <__TextAreaV2
        ref={ref as any}
        size={mappedSize}
        variant={mappedVariant as any}
        className={className}
        error={error as any}
        {...(rest as any)}
      />
      <div className="flex justify-between items-center">
        <div className="flex-1">
          {description && !error && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">{error}</p>
          )}
        </div>
        {showCharCount && maxLength && (
          <p className={cn(
            'text-xs tabular-nums',
            currentLength >= maxLength * 0.9 && currentLength < maxLength && 'text-amber-500',
            currentLength >= maxLength && 'text-destructive',
            currentLength < maxLength * 0.9 && 'text-muted-foreground'
          )}>
            {currentLength}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
});
ExportedTextArea.displayName = 'TextArea';
// PHASE-13 END

export { ExportedTextArea as TextArea, textareaVariants };
